// Design tokens for the 補貨 tab, transcribed from the approved prototype
// (scratchpad/design_b_shelfmap.html, "the counter as a map"). They live here rather than in the
// MUI theme because this one tab deliberately looks like a paper counter sheet, not like the rest
// of the OMS, and the palette must not drift when the app theme changes.
import { DoneKind, LaneKey, MoveKind, SeasonKey, TierKey, TileState } from './types';

export const C = {
  paper: '#F4EFE6',
  paper2: '#E9E1D4',
  card: '#FFFDF9',
  wash: '#FBF8F2',
  ink: '#191611',
  ink2: '#5A5247',
  ink3: '#93897A',
  line: '#DCD2C1',
  line2: '#C9BCA6',
  onInk: '#F6F1E7',
  onInk2: '#BDB2A0',
  // one colour per verb (absorbed from candidate A): green 掛上架 · orange 帶回 · blue 去拿 · ochre 等到貨
  go: '#2C6647',
  goWash: '#E4EFE7',
  back: '#BF4218',
  backWash: '#FBE9E1',
  fetch: '#35536E',
  fetchWash: '#E5ECF2',
  wait: '#8E6A12',
  waitWash: '#F7EEDA',
} as const;

export const MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';
export const RADIUS = 12;
export const SHADOW = '0 1px 0 rgba(25,22,17,.04), 0 2px 8px rgba(25,22,17,.06)';

export const ZONE_NUMERALS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧'];

export interface VerbToken {
  // 上 / 拿 / 回 / 等 — the two-pixel-wide word on the map pill and the card's verb badge.
  mark: string;
  label: string;
  action: string;
  colour: string;
  wash: string;
}

// One verb per move kind, one colour per verb, used on the verb pill, the button and the map pill.
export const VERB: Record<MoveKind, VerbToken> = {
  display: { mark: '上', label: '掛上架', action: '確認已上架', colour: C.go, wash: C.goWash },
  from_office: { mark: '拿', label: '去辦公室拿', action: '確認已帶到門市', colour: C.fetch, wash: C.fetchWash },
  bring_back: { mark: '回', label: '帶回辦公室', action: '帶回了', colour: C.back, wash: C.backWash },
  on_arrival: { mark: '等', label: '等到貨再帶回', action: '到貨後再做', colour: C.wait, wash: C.waitWash },
  waiting: { mark: '等', label: '等到貨', action: '到貨了', colour: C.wait, wash: C.waitWash },
};

export const SEASON_LABEL: Record<SeasonKey, string> = { AW: '秋', SS: '夏', ALL: '全年' };

export const SEASON_COLOUR: Record<SeasonKey, { colour: string; wash: string }> = {
  AW: { colour: C.wait, wash: C.waitWash },
  SS: { colour: C.fetch, wash: C.fetchWash },
  ALL: { colour: C.ink2, wash: C.paper2 },
};

export const ASKED_LABEL = '客人問過';

export const LANE_LABEL: Record<LaneKey, string> = {
  core: '主力', new_season: '新季款', lead: '線索款', warehouse: '倉庫',
};

// 訂貨 tier badges. `seasonal` is the v2 spelling of the 新季款 tier and must keep rendering.
export const TIER_ORDER: TierKey[] = ['core', 'new_season', 'lead'];

export const TIER_BADGE: Record<TierKey, { label: string; short: string; colour: string; wash: string }> = {
  core: { label: '主力 · 一定要補', short: '主力', colour: C.back, wash: C.backWash },
  new_season: { label: '新季款 · 先鋪廣度', short: '新季款', colour: C.wait, wash: C.waitWash },
  seasonal: { label: '新季款 · 先鋪廣度', short: '新季款', colour: C.wait, wash: C.waitWash },
  lead: { label: '線索款 · 線上在賣', short: '線索款', colour: C.fetch, wash: C.fetchWash },
};

export const TIER_NOTE: Record<TierKey, string> = {
  core: '門市已驗證 → 每個賣過的顏色一組，再依銷量補深',
  new_season: '上架 45 天內的新季款 · 門市還沒賣過先放一組（一色的主要尺寸）',
  seasonal: '上架 45 天內的新季款 · 門市還沒賣過先放一組（一色的主要尺寸）',
  lead: '線上熱賣、門市還沒證明 → 先放一組試賣',
};

// Rack tiles. The badge is what the staff reads off the rail: 缺 N / ✓ / 帶回 N / 到貨中.
export const TILE_BADGE: Record<TileState, { label: string; note: string }> = {
  short: { label: '缺', note: '門市有貨，架上少了' },
  ok: { label: '✓', note: '留在架上' },
  remove: { label: '帶回', note: '帶回辦公室' },
  incoming: { label: '到貨中', note: '新貨到了上這裡' },
};

// How many tiles of each state the rack sheet shows before 「全部展開」 — the first screen has to be
// a balanced picture of the rack, so the gaps cannot crowd out the ✓ and the 帶回.
export const TILE_QUOTA: Record<TileState, number> = { short: 6, incoming: 3, ok: 6, remove: 3 };

// ---- the floor plan -------------------------------------------------------------------------
// Real positions in the NOKE counter, door at the bottom. Fixture ids come from the backend's
// DEFAULT_FIXTURES (noke-merchandising-validation.js); an id not listed here still renders, as a
// full-width zone appended after the plan.
export interface ZoneSlot {
  // Named grid area, so the plan is one declarative template instead of six row/column pairs —
  // and so a zone card can size to its content instead of being stretched by its neighbours.
  area: string;
  // A shelf (層架/島台) is drawn with a double rule, a hanging rail with a single one.
  shelf?: boolean;
}

export const FLOOR_SLOTS: Record<string, ZoneSlot> = {
  accessories: { area: 'back', shelf: true },
  tops_left: { area: 'left' },
  pants_a: { area: 'pa' },
  pants_b: { area: 'pb' },
  tops_right: { area: 'right' },
  mdoc: { area: 'mdoc', shelf: true },
};

// Door at the bottom: you walk in past the MdoC island, down the middle, and the bag shelf is the
// back wall. Same template on both layouts so the picture the staff learns never changes.
export const FLOOR_TEMPLATE = [
  '"back back back"',
  '"left pa right"',
  '"left pb right"',
  '"mdoc mdoc mdoc"',
].join(' ');

// Walk order: in at the door, MdoC island first, the back shelf last.
export const WALK_ORDER = ['mdoc', 'tops_left', 'pants_a', 'pants_b', 'tops_right', 'accessories'];

// Narrow zones (the two middle pants rails) fit fewer thumbnails than the tall side rails.
export const NARROW_ZONES = ['pants_a', 'pants_b'];

// ---- colour swatches ------------------------------------------------------------------------
// One product photo often serves five colourways (SKU 2217 ships one image for 黑色/灰色/深米色),
// so the photo alone cannot tell a new part-timer which garment to grab. A dot next to the colour
// word closes that gap. Only colours we can name with certainty get a dot — an unknown or patterned
// name gets none, because a wrong dot is worse than no dot.
const SWATCH: Record<string, string> = {
  黑色: '#17120D', 墨色: '#2B2B2B', 炭灰: '#3A3A3A', 深灰: '#4A4A4A', 灰色: '#8E8E8E',
  藍灰: '#6E7C91', 淺灰色: '#C4C4C4', 淺灰: '#C4C4C4', 灰米白: '#E7E1D5', 白色: '#FBFAF7',
  象牙白: '#F2E9D8', 象牙色: '#F2E9D8', 燕麥米色: '#DFD2B8', 燕麥色: '#DFD2B8',
  淺米色: '#EEE0C6', 米色: '#E2D2B4', 深米色: '#C9AF86', 卡其色: '#A78F5F',
  棕色: '#6B4429', 咖啡色: '#4A2F1E', 深藍: '#1F2D4D', 海軍藍: '#1B2A4A',
  淺天藍色: '#B8D6EE', 天空藍: '#93BEDF', 薄荷色: '#A8DCC6', 淡綠色: '#BCD8B0',
  深綠色: '#1E4632', 深綠: '#1E4632', 軍綠: '#4B5320', 灰綠色: '#7D8C7A',
  酒紅: '#6B2233', 磚紅: '#A2472B',
};

const SWATCH_KEYS = Object.keys(SWATCH).sort((a, b) => b.length - a.length);
const PATTERNED = /條紋|格紋|印花|拼接|迷彩/;

export function swatchHex(name?: string | null): string | null {
  if (!name) return null;
  if (PATTERNED.test(name)) return null;
  for (let index = 0; index < SWATCH_KEYS.length; index += 1) {
    if (name.indexOf(SWATCH_KEYS[index]) >= 0) return SWATCH[SWATCH_KEYS[index]];
  }
  return null;
}

// A near-white dot needs a border of its own or it disappears into the card.
export function swatchNeedsRule(hex: string): boolean {
  const sum = parseInt(hex.slice(1, 3), 16) + parseInt(hex.slice(3, 5), 16) + parseInt(hex.slice(5, 7), 16);
  return sum > 600;
}

// ---- 排除 (the owner's veto) ------------------------------------------------------------------
// 今天不做 is per MOVE, so the fold has to name which move was skipped rather than the style: the
// same colour can be skipped for 上架 and still be due for 帶回.
export const TODAY_SKIP_LABEL: Record<DoneKind, string> = {
  display: '今天不上架', from_office: '今天不去辦公室拿', bring_back: '今天不帶回', received: '今天不點收',
};

/** 「已排除 3 款 · 查看」 / 「今天略過 2 件 · 查看」 — one wording, used by all three screens. */
export const hiddenFoldLabel = (kind: 'plan' | 'today', count: number): string => (
  kind === 'plan' ? `已排除 ${count} 款 · 查看` : `今天略過 ${count} 件 · 查看`
);
